import { Injectable } from "@angular/core";
import { BaseValidationResult } from "@ha/ui/forms";
import { Action, State, StateContext, Store } from "@ngxs/store";
import { produce } from "immer";
import { EMPTY } from "rxjs";
import { tap } from "rxjs";

import { PermitPerson } from "../models/permit-person.model";
import { PermitRequestCandidate } from "../models/permit-request-candidate.model";
import { PermitRequest } from "../models/permit-request.model";
import { PermitToSign } from "../models/permit-to-sign.model";
import { PermitUserService } from "../services/permit-user.service";
import { PermitUserActions } from "./permit-user.action";

export interface PermitUserStateModel {
    currentPerson: PermitPerson;
    permitsToSign: PermitToSign[];
    permitRequests: PermitRequest[];
    isHotWorker: boolean;
    canCreateDemoPermits: boolean;
    isExternal: boolean;
    isAdministrator: boolean;
    loading: boolean;
    loadingPermitRequestCandidates: boolean;
    permitRequestCandidates: PermitRequestCandidate[];
    selectedPermitRequestCandidate: PermitRequestCandidate;
    requestingPermit: boolean;
    permitRequestSubmitted: boolean;
    permitRequestErrorMessage: string;
}

@Injectable()
@State<PermitUserStateModel>({
    name: "permitUser",
    defaults: {
        currentPerson: undefined,
        permitsToSign: undefined,
        permitRequests: undefined,
        isHotWorker: undefined,
        canCreateDemoPermits: undefined,
        isExternal: undefined,
        isAdministrator: undefined,
        loading: false,
        loadingPermitRequestCandidates: false,
        permitRequestCandidates: undefined,
        selectedPermitRequestCandidate: undefined,
        requestingPermit: false,
        permitRequestSubmitted: false,
        permitRequestErrorMessage: undefined,
    },
})
export class PermitUserState {
    constructor(private permitUserService: PermitUserService, private store: Store) {}

    @Action(PermitUserActions.GetCurrentPerson)
    public getCurrentPerson(ctx: StateContext<PermitUserStateModel>) {
        return this.permitUserService.getCurrentPerson().pipe(tap((result) => {
            ctx.setState(produce((draft) => {
                draft.currentPerson = result;
                draft.isHotWorker = !!this.permitUserService.currentUser.isHotworker;
                draft.canCreateDemoPermits = !!this.permitUserService.currentUser.canCreateDemoPermits;
                draft.isExternal = !!this.permitUserService.currentUser.isExternal;
                draft.isAdministrator = !!this.permitUserService.currentUser.isAdministrator;
            }));
        }));
    }

    @Action(PermitUserActions.GetPermitsToSign)
    public getPermitsToSign(ctx: StateContext<PermitUserStateModel>) {
        return this.permitUserService.getPermitsToSign().pipe(tap((result) => {
            ctx.setState(produce((draft) => {
                draft.permitsToSign = result;
            }));
        }));
    }

    @Action(PermitUserActions.GetPermitRequests)
    public getPermitRequests(ctx: StateContext<PermitUserStateModel>) {
        ctx.patchState({
            loading: true,
        });
        return this.permitUserService.getPermitRequests().pipe(tap((result) => {
            ctx.setState(produce((draft) => {
                draft.permitRequests = result;
                draft.loading = false;
            }));
        }));
    }

    @Action(PermitUserActions.GetPermitRequestCandidates)
    public getPermitRequestCandidates(ctx: StateContext<PermitUserStateModel>, action: PermitUserActions.GetPermitRequestCandidates) {
        ctx.setState(produce((draft) => {
            draft.loadingPermitRequestCandidates = true;
            draft.permitRequestCandidates = undefined;
            draft.selectedPermitRequestCandidate = undefined;
            draft.permitRequestErrorMessage = undefined;
            draft.permitRequestSubmitted = false;
        }));

        return this.permitUserService.getPermitRequestCandidates(action.permitId).pipe(tap({
            next: (result) => {
                ctx.setState(produce((draft) => {
                    draft.loadingPermitRequestCandidates = false;
                    draft.permitRequestCandidates = result;
                    draft.selectedPermitRequestCandidate = undefined;
                    draft.permitRequestErrorMessage = undefined;
                    draft.permitRequestSubmitted = false;
                }));
            },
            error: () => {
                ctx.setState(produce((draft) => {
                    draft.loadingPermitRequestCandidates = false;
                }));
            },
        }));
    }

    @Action(PermitUserActions.SelectPermitRequestCandidate)
    public selectPermitRequestCandidate(ctx: StateContext<PermitUserStateModel>, action: PermitUserActions.SelectPermitRequestCandidate): void {
        const currentState = ctx.getState();
        const currentSelected = currentState.selectedPermitRequestCandidate;
        const isAlreadySelected = currentSelected?.personId === action.personId;
        const selectedCandidate = isAlreadySelected
            ? undefined
            : currentState.permitRequestCandidates?.find((_) => _.personId === action.personId);

        ctx.setState(produce((draft) => {
            draft.selectedPermitRequestCandidate = selectedCandidate;
            draft.permitRequestErrorMessage = undefined;
        }));
    }

    @Action(PermitUserActions.RequestPermit)
    public requestPermit(ctx: StateContext<PermitUserStateModel>, action: PermitUserActions.RequestPermit) {
        const selectedCandidate = ctx.getState().selectedPermitRequestCandidate;

        if (!selectedCandidate) {
            return EMPTY;
        }

        ctx.setState(produce((draft) => {
            draft.requestingPermit = true;
            draft.permitRequestSubmitted = false;
            draft.permitRequestErrorMessage = undefined;
        }));

        return this.permitUserService.requestPermit({
            permitId: action.permitId,
            personId: selectedCandidate.personId,
        }).pipe(tap((result: BaseValidationResult) => {
            ctx.setState(produce((draft) => {
                draft.requestingPermit = false;
                draft.permitRequestSubmitted = result?.success === true;
                draft.permitRequestErrorMessage = result?.success === true
                    ? undefined
                    : result?.validationResult?.errors?.[0]?.errorMessage;
            }));
        }));
    }

    @Action(PermitUserActions.ResetPermitRequestFlow)
    public resetPermitRequestFlow(ctx: StateContext<PermitUserStateModel>): void {
        ctx.setState(produce((draft) => {
            draft.loadingPermitRequestCandidates = false;
            draft.permitRequestCandidates = undefined;
            draft.selectedPermitRequestCandidate = undefined;
            draft.requestingPermit = false;
            draft.permitRequestSubmitted = false;
            draft.permitRequestErrorMessage = undefined;
        }));
    }
}
